import { useTheme } from '../../context/ThemeContext';
import { Activity, AlertCircle, CheckCircle2 } from 'lucide-react';
import { HealthEventOutcome, HealthEventSource, type HealthEvent } from '../../gql/types';
import { formatRelative } from '../../lib/format';

// Per-provider recent-events panel rendered inside the ProviderFormModal. Reads the
// in-memory ring buffer surfaced by the `Provider.recentHealthEvents` GraphQL field — the
// HealthServer GenServer's recent observations of probes and live dispatch outcomes. Gives
// operators a "why is this provider degrading?" view next to the configuration knobs that
// control its probing without making them scrape container logs.

const SOURCE_TONE: Record<
  HealthEventSource,
  { darkBg: string; lightBg: string; label: string }
> = {
  [HealthEventSource.Probe]: {
    darkBg: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    lightBg: 'bg-sky-50 text-sky-700 border-sky-200',
    label: 'probe',
  },
  [HealthEventSource.Live]: {
    darkBg: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
    lightBg: 'bg-violet-50 text-violet-700 border-violet-200',
    label: 'live',
  },
};

function SourceChip({ source }: { source: HealthEventSource }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const tone = SOURCE_TONE[source];
  return (
    <span
      className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-widest ${
        isDark ? tone.darkBg : tone.lightBg
      }`}
    >
      {tone.label}
    </span>
  );
}

function OutcomeIcon({ outcome }: { outcome: HealthEventOutcome }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  if (outcome === HealthEventOutcome.Ok) {
    return (
      <CheckCircle2
        className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}
        aria-label="ok"
      />
    );
  }
  return (
    <AlertCircle
      className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-rose-300' : 'text-rose-600'}`}
      aria-label="failed"
    />
  );
}

function EventRow({ event }: { event: HealthEvent }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isFailure = event.outcome === HealthEventOutcome.Failed;

  return (
    <li
      data-testid="recent-health-event"
      data-outcome={event.outcome}
      data-source={event.source}
      className={`flex items-start gap-2 rounded-lg border p-2.5 ${
        isDark
          ? isFailure
            ? 'border-rose-500/20 bg-rose-500/[0.04]'
            : 'border-white/[0.06] bg-white/[0.02]'
          : isFailure
            ? 'border-rose-200 bg-rose-50/40'
            : 'border-slate-200 bg-slate-50'
      }`}
    >
      <OutcomeIcon outcome={event.outcome} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="inline-flex items-center gap-1.5">
            <SourceChip source={event.source} />
            {event.statusCode != null && (
              <span
                className={`font-mono text-[10px] ${
                  isDark ? 'text-white/50' : 'text-slate-500'
                }`}
              >
                HTTP {event.statusCode}
              </span>
            )}
            {event.latencyMs != null && (
              <span
                className={`font-mono text-[10px] ${
                  isDark ? 'text-white/40' : 'text-slate-400'
                }`}
              >
                {event.latencyMs} ms
              </span>
            )}
          </span>
          <span
            className={`flex-shrink-0 text-[10px] ${
              isDark ? 'text-white/40' : 'text-slate-400'
            }`}
            title={event.at}
          >
            {formatRelative(event.at)}
          </span>
        </div>
        {event.url && (
          <span
            className={`mt-0.5 block truncate font-mono text-[11px] ${
              isDark ? 'text-white/60' : 'text-slate-600'
            }`}
            title={event.url}
          >
            {event.url}
          </span>
        )}
        {event.reason && (
          <span
            className={`mt-0.5 block truncate font-mono text-[11px] ${
              isDark ? 'text-rose-300' : 'text-rose-700'
            }`}
            title={event.reason}
          >
            {event.reason}
          </span>
        )}
      </div>
    </li>
  );
}

export interface RecentHealthEventsProps {
  events: HealthEvent[];
}

export function RecentHealthEvents({ events }: RecentHealthEventsProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (events.length === 0) {
    return (
      <div
        className={`rounded-xl border p-4 text-xs ${
          isDark
            ? 'border-white/[0.06] bg-white/[0.02] text-white/40'
            : 'border-slate-200 bg-slate-50 text-slate-400'
        }`}
      >
        <span className="inline-flex items-center gap-1.5">
          <Activity className="h-3.5 w-3.5" aria-hidden="true" />
          No recent activity recorded for this provider. The ring buffer is in-memory and
          resets on every server restart.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
        Most recent {events.length} signal{events.length === 1 ? '' : 's'} (probes + live
        dispatches), newest first. In-memory only — resets on server restart.
      </p>
      <ul className="space-y-1.5">
        {events.map((event, i) => (
          // Composite key: `at` is second-precision so multiple events in the same second
          // would collide on key alone.
          <EventRow key={`${event.at}-${i}`} event={event} />
        ))}
      </ul>
    </div>
  );
}
